import { useEffect, useRef } from "react";
import Hls from "hls.js";

type Props = {
  src: string;
  mini?: boolean;
};

/** Plays an OddsData .m3u8 in our page. Their page stays on the iframe path. */
export default function HlsPlayer({ src, mini = false }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    let player: Hls | null = null;
    let stopped = false;

    const start = () => {
      if (stopped) return;
      video.play().catch(() => undefined);
    };

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.addEventListener("loadedmetadata", start);
    } else if (Hls.isSupported()) {
      player = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        liveSyncDurationCount: 3,
        maxBufferLength: 20,
        backBufferLength: 30,
      });
      player.loadSource(src);
      player.attachMedia(video);
      player.on(Hls.Events.MANIFEST_PARSED, start);
      player.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal || !player) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) player.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) player.recoverMediaError();
        else player.destroy();
      });
    }

    return () => {
      stopped = true;
      video.removeEventListener("loadedmetadata", start);
      player?.destroy();
      video.removeAttribute("src");
      video.load();
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      muted
      autoPlay
      playsInline
      controls
      style={{
        width: "100%",
        height: mini ? "100%" : window.innerWidth <= 786 ? "57vw" : "206px",
        minHeight: mini ? 0 : window.innerWidth <= 786 ? "57vw" : "206px",
        background: "#000",
        display: "block",
        objectFit: "contain",
      }}
    />
  );
}
